// AccountModal — C-ACCOUNT, profile and security only.
//
// Ported from the 2026-08-26 design drop,
// handoff/commerce-app/src/components/AccountModal.tsx. The dialog shape, the
// sidebar of tabs with the avatar above them, the sign-out at the foot, and the
// two-column field layout are the handoff's.
//
// ADAPTED — two of the four tabs are not here (Owner D7/D8):
//
// * NOTIFICATIONS. Four switches over "Invoice issued", "Metering delivery
//   problems", "Usage threshold alerts", "Product updates". This service sends
//   none of those emails and stores no preference about them, so every switch
//   would be a control over nothing, and "Save preferences" would save nothing.
// * SESSIONS. "This browser · Chrome · Houston, TX · active now", an iPhone to
//   revoke, and "Sign out everywhere". The refresh-family table could support a
//   version of this one day, but it records no device, no browser and no
//   location, and inventing them is not a smaller lie for being plausible.
//
// The shell's user menu used to offer "Notifications" as an entry, opening the
// profile tab because the tab it named does not exist. That entry is gone
// (M-W38-02) and the menu now offers only the two tabs below.
//
// ADAPTED — the security tab links out rather than changing a password itself.
// allauth already owns the password change and the address change on the
// server-rendered side: both send mail, both handle verification, and both work
// today. A second implementation in the portal would be a second place for an
// account recovery to be subtly wrong. The handoff's two-step-verification row
// is gone entirely: no MFA is installed, so an "Enable" button would lead
// nowhere, and "Password last changed Mar 12, 2026" is not recorded.
//
// ADAPTED — the profile tab's "Job title" input is gone (nothing stores one)
// and the "Billing Admin" badge with it (no role exists — see UsersPage). The
// email address is shown and is not editable here, for the reason above. The
// name IS editable, because `first_name` is a real field and it is what the
// Users page shows.
import { useEffect, useState, type FormEvent } from 'react';
import { ExternalLink, Lock, LogOut, User, type LucideIcon } from 'lucide-react';
import { Avatar, AvatarFallback } from '@miq/design-system/components/ui/avatar';
import { Button } from '@miq/design-system/components/ui/button';
import { Dialog, DialogContent } from '@miq/design-system/components/ui/dialog';
import { Input } from '@miq/design-system/components/ui/input';
import { Label } from '@miq/design-system/components/ui/label';
import { Separator } from '@miq/design-system/components/ui/separator';

import { onDay } from '../data/instants';
import { usePortalSession } from '../auth/context';
import {
  PortalDataFailed,
  fetchAccount,
  saveAccountName,
  type Account,
} from '../data/subscriptions';

/** The two tabs that exist. The shell may ask for either. */
export type AccountTab = 'profile' | 'security';

const TABS: { id: AccountTab; label: string; icon: LucideIcon }[] = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'security', label: 'Password & security', icon: Lock },
];

export interface AccountModalProps {
  open: boolean;
  tab?: AccountTab;
  onOpenChange: (open: boolean) => void;
  onSignOut: () => void;
}

export function AccountModal({
  open,
  tab = 'profile',
  onOpenChange,
  onSignOut,
}: AccountModalProps) {
  const { authorizedFetch, session } = usePortalSession();
  const [active, setActive] = useState<AccountTab>(tab);
  const [account, setAccount] = useState<Account | null>(null);
  const [refusal, setRefusal] = useState('');

  // ADAPTED: the handoff syncs its active tab from the prop inside an effect.
  // The repo's lint refuses a synchronous setState in an effect, and rightly —
  // it is a cascading render for something the parent can express instead. So
  // the tab initialises this state, and PortalApp gives the modal a key that
  // changes each time it is opened, which remounts it. Same behaviour, no
  // render cascade, and one fewer place where two sources of truth for "which
  // tab" can disagree.

  // Read on open rather than on mount: a name changed and re-opened should show
  // the change.
  useEffect(() => {
    if (!open) {
      return;
    }
    let current = true;

    (async () => {
      try {
        const read = await fetchAccount(authorizedFetch);
        if (current) {
          setAccount(read);
        }
      } catch (failure) {
        if (!(failure instanceof PortalDataFailed)) {
          throw failure;
        }
        if (current) {
          setRefusal(failure.message);
        }
      }
    })();

    return () => {
      current = false;
    };
  }, [open, authorizedFetch]);

  const email =
    account?.user.email ??
    (session.status === 'signed-in' ? session.identity.user.email : '');
  const initials = email.slice(0, 2).toUpperCase();

  return (
    // ADAPTED: the DS Dialog is React Aria's DialogTrigger, so it takes
    // `isOpen` where the handoff (built on shadcn) passes `open`.
    <Dialog isOpen={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[540px] w-[780px] max-w-[780px] gap-0 overflow-hidden p-0">
        {/* ADAPTED: the handoff's DialogContent renders its children directly,
            so a `flex` class on it laid the sidebar beside the panel. The DS
            DialogContent puts a React Aria Dialog between the two, so the flex
            row has to be inside it — without this wrapper the sidebar stacks
            above the content and the modal renders as two bands. */}
        <div className="flex h-full min-h-0">
          <aside className="flex w-56 shrink-0 flex-col gap-0.5 border-r bg-muted/40 p-3">
            <div className="flex items-center gap-2.5 px-2 pb-3">
              <Avatar className="size-10">
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 leading-tight">
                {/* ADAPTED: the fixture has a name and a title. Where somebody
                  gave no name, the address is the honest first line. */}
                <div className="truncate text-sm font-semibold">
                  {account?.user.name || email}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {account?.customer.display_name ?? ''}
                </div>
              </div>
            </div>
            {TABS.map(({ id, label, icon: TabIcon }) => (
              <Button
                key={id}
                variant={active === id ? 'secondary' : 'ghost'}
                className="justify-start gap-2.5"
                onPress={() => setActive(id)}
              >
                <TabIcon className="size-4" /> {label}
              </Button>
            ))}
            <div className="mt-auto border-t pt-2">
              <Button
                variant="ghost"
                className="w-full justify-start gap-2.5"
                onPress={() => {
                  onOpenChange(false);
                  onSignOut();
                }}
              >
                <LogOut className="size-4" /> Sign out
              </Button>
            </div>
          </aside>
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="border-b px-5 py-4 text-base font-semibold">
              {TABS.find((entry) => entry.id === active)?.label}
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {refusal && (
                <p role="alert" className="text-sm text-destructive">
                  {refusal}
                </p>
              )}
              {!account && !refusal && (
                <p className="text-sm text-muted-foreground">Reading your account…</p>
              )}
              {account && active === 'profile' && (
                <ProfileTab
                  account={account}
                  onSaved={(name) =>
                    setAccount({ ...account, user: { ...account.user, name } })
                  }
                />
              )}
              {account && active === 'security' && <SecurityTab account={account} />}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProfileTab({
  account,
  onSaved,
}: {
  account: Account;
  onSaved: (name: string) => void;
}) {
  const { authorizedFetch } = usePortalSession();
  const [name, setName] = useState(account.user.name);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [refusal, setRefusal] = useState('');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setRefusal('');
    setSaved(false);
    setSaving(true);
    try {
      await saveAccountName(authorizedFetch, name);
      onSaved(name);
      setSaved(true);
    } catch (failure) {
      if (!(failure instanceof PortalDataFailed)) {
        throw failure;
      }
      setRefusal(failure.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <div className="flex items-center gap-3.5">
        <Avatar className="size-12">
          <AvatarFallback>{account.user.email.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div>
          <div className="text-sm font-semibold">
            {account.user.name || account.user.email}
          </div>
          <div className="text-xs text-muted-foreground">
            {account.customer.display_name} · with us since{' '}
            {onDay(account.membership.joined)}
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="account-name">Your name</Label>
        <Input
          id="account-name"
          name="name"
          autoComplete="name"
          maxLength={account.name_limit}
          placeholder="How your colleagues see you"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Stored as you type it — we do not split it into a first and last name, because
          which word is which is not ours to guess. This is what your colleagues see on
          the Users page.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="account-email">Work email</Label>
        {/* ADAPTED: the DS Input is React Aria's, which takes `readOnly` on the
            underlying element rather than React Aria's `isReadOnly`. */}
        <Input id="account-email" value={account.user.email} readOnly disabled />
        <p className="text-xs text-muted-foreground">
          Your email is your sign-in ID. Changing it needs a verification round trip,
          which lives on its own page.
        </p>
        <a
          href={account.elsewhere.email_addresses}
          className="inline-flex items-center gap-1.5 text-sm text-link underline-offset-4 hover:underline"
        >
          Manage email addresses <ExternalLink className="size-3" />
        </a>
      </div>

      <Separator />

      {refusal && (
        <p role="alert" className="text-sm text-destructive">
          {refusal}
        </p>
      )}
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">{saved ? 'Saved.' : ' '}</span>
        <Button type="submit" size="sm" isPending={saving}>
          {saving ? 'Saving…' : 'Save profile'}
        </Button>
      </div>
    </form>
  );
}

function SecurityTab({ account }: { account: Account }) {
  return (
    <div className="space-y-4 text-sm">
      {/* Says nothing about how THIS person signed in. It used to open
          "Password sign-in is active", which was shown to everybody including
          somebody who had just come through the federated door — and
          GET /portal/api/account/ reports no sign-in method, so the screen had
          no way to know. What is true for everyone is where each route is
          changed from, and that is what it says. */}
      <p className="text-xs text-muted-foreground">
        Your password is managed here. Where your company has single sign-on configured
        with us, that route is set up by agreement rather than from this page.
      </p>

      <div>
        <div className="font-semibold">Change your password</div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Opens the page that owns it — the same one this service has always used, so a
          password changed there is changed everywhere you sign in.
        </p>
        <a
          href={account.elsewhere.password_change}
          className="mt-2 inline-flex items-center gap-1.5 text-sm text-link underline-offset-4 hover:underline"
        >
          Change password <ExternalLink className="size-3" />
        </a>
      </div>

      <Separator />

      <div>
        <div className="font-semibold">Forgotten it?</div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          We email a reset link to {account.user.email}.
        </p>
        <a
          href={account.elsewhere.password_reset}
          className="mt-2 inline-flex items-center gap-1.5 text-sm text-link underline-offset-4 hover:underline"
        >
          Send a reset link <ExternalLink className="size-3" />
        </a>
      </div>

      <Separator />

      <p className="text-xs text-muted-foreground">
        Signing out ends this browser&apos;s session immediately — the token this page is
        holding stops working at once rather than at its expiry.
      </p>
    </div>
  );
}
